import { create } from 'zustand';

import {
    DEFAULT_ANCHOR_CHAIN_ID,
    isAnchorChainId,
    type AnchorChainId,
} from './config/anchors';
import type { RuntimeMode, WalletSession } from './types';

export const APP_SESSION_STORAGE_KEY = 'snappy.app.session.v2';
export const ANCHOR_CHAIN_STORAGE_KEY = 'snappy.wallet.anchorChainId.v1';
export const LEGACY_SESSION_STORAGE_KEY = 'inout.wallet.session';
const EOA_ADDRESS_PATTERN = /^0x[0-9a-fA-F]{40}$/u;
const SESSION_ID_PATTERN = /^0x[0-9a-fA-F]{64}$/u;

export interface AppSessionCorrelation {
    owner: string;
    /**
     * Only signed trading sessions mint one. A legacy `POST /login` token knows
     * which EOA signed for it but has no session to point at.
     */
    sessionId?: string;
}

function isWalletSession(value: unknown): value is WalletSession {
    if (!value || typeof value !== 'object') return false;
    const obj = value as Record<string, unknown>;
    if (
        typeof obj.token !== 'string' ||
        typeof obj.issuedAt !== 'number' ||
        typeof obj.expiresAt !== 'number'
    ) {
        return false;
    }
    const hasOwner = obj.owner !== undefined;
    const hasSessionId = obj.sessionId !== undefined;
    // An owner alone is a complete answer to "who signed for this token"; a
    // session ID without one names nothing and stays invalid.
    if (hasSessionId && !hasOwner) return false;
    if (
        hasOwner &&
        !(typeof obj.owner === 'string' && EOA_ADDRESS_PATTERN.test(obj.owner))
    ) {
        return false;
    }
    if (
        hasSessionId &&
        !(
            typeof obj.sessionId === 'string' &&
            SESSION_ID_PATTERN.test(obj.sessionId)
        )
    ) {
        return false;
    }
    return true;
}

/**
 * Correlation is metadata, never the token itself: a malformed value is dropped
 * rather than written, so a persisted session always reads back as it was saved.
 */
function normalizeCorrelation(
    correlation: AppSessionCorrelation | undefined,
): AppSessionCorrelation | undefined {
    if (!correlation || !EOA_ADDRESS_PATTERN.test(correlation.owner)) {
        return undefined;
    }
    const { owner, sessionId } = correlation;
    if (sessionId === undefined) return { owner };
    return SESSION_ID_PATTERN.test(sessionId)
        ? { owner, sessionId }
        : { owner };
}

export function loadStoredSession(): WalletSession | null {
    try {
        const raw = localStorage.getItem(APP_SESSION_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as unknown;
        return isWalletSession(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

export function saveStoredAppSession(
    token: string,
    expiresAtMs: number,
    issuedAtMs = Date.now(),
    correlation?: AppSessionCorrelation,
): WalletSession {
    const session: WalletSession = {
        token,
        issuedAt: issuedAtMs,
        expiresAt: expiresAtMs,
        ...(normalizeCorrelation(correlation) ?? {}),
    };
    localStorage.setItem(APP_SESSION_STORAGE_KEY, JSON.stringify(session));
    return session;
}

/**
 * Kept as an internal convenience for callers that receive a TTL. `owner` is the
 * address the caller authenticated as; persisting it is what lets a later wallet
 * switch be detected on a legacy `POST /login` session.
 */
export function saveStoredSession(
    token: string,
    expiresInSeconds: number,
    owner?: string,
): WalletSession {
    const issuedAtMs = Date.now();
    return saveStoredAppSession(
        token,
        issuedAtMs + expiresInSeconds * 1000,
        issuedAtMs,
        owner === undefined ? undefined : { owner },
    );
}

export function clearStoredSession(): void {
    localStorage.removeItem(APP_SESSION_STORAGE_KEY);
    localStorage.removeItem(LEGACY_SESSION_STORAGE_KEY);
}

/**
 * The anchor the user last actually used, so a returning user is not silently
 * moved to the default one. Storage can be unavailable (private mode, blocked
 * site data), and a preference is never worth failing a render over.
 */
export function loadStoredAnchorChainId(): AnchorChainId | null {
    try {
        const raw = localStorage.getItem(ANCHOR_CHAIN_STORAGE_KEY);
        return isAnchorChainId(raw) ? (Number(raw) as AnchorChainId) : null;
    } catch {
        return null;
    }
}

export function saveStoredAnchorChainId(chainId: AnchorChainId): void {
    try {
        localStorage.setItem(ANCHOR_CHAIN_STORAGE_KEY, String(chainId));
    } catch {
        /* a remembered preference is not worth a thrown render */
    }
}

export function isSessionExpired(
    session: WalletSession,
    now = Date.now(),
): boolean {
    return session.expiresAt <= now;
}

interface WalletStoreState {
    session: WalletSession | null;
    runtimeMode: RuntimeMode;
    /**
     * The authorization anchor in force: last used, or the one the user picked
     * this session. It is a PREFERENCE, not the resolved answer — `useAnchorChain`
     * resolves it against the connected wallet's chain.
     */
    anchorChainId: AnchorChainId;
    /**
     * The user chose this anchor explicitly (the session/onboarding selector),
     * so it outranks whatever chain the wallet happens to be on.
     */
    anchorPickedByUser: boolean;
    setAnchorChain: (chainId: AnchorChainId, pickedByUser?: boolean) => void;
    saveSession: (
        token: string,
        expiresInSeconds: number,
        owner?: string,
    ) => WalletSession;
    saveAppSession: (
        token: string,
        expiresAtMs: number,
        correlation?: AppSessionCorrelation,
    ) => WalletSession;
    clearSession: () => void;
    reloadSession: () => void;
    setRuntimeMode: (mode: RuntimeMode) => void;
}

export const useWalletStore = create<WalletStoreState>((set) => ({
    session: loadStoredSession(),
    runtimeMode: 'demo',
    anchorChainId: loadStoredAnchorChainId() ?? DEFAULT_ANCHOR_CHAIN_ID,
    anchorPickedByUser: false,
    setAnchorChain: (anchorChainId, pickedByUser = true) => {
        saveStoredAnchorChainId(anchorChainId);
        set({ anchorChainId, anchorPickedByUser: pickedByUser });
    },
    saveSession: (token, expiresInSeconds, owner) => {
        const session = saveStoredSession(token, expiresInSeconds, owner);
        set({ session });
        return session;
    },
    saveAppSession: (token, expiresAtMs, correlation) => {
        const session = saveStoredAppSession(
            token,
            expiresAtMs,
            Date.now(),
            correlation,
        );
        set({ session });
        return session;
    },
    clearSession: () => {
        clearStoredSession();
        // the anchor preference outlives a sign-out on purpose: it is where the
        // user's wallet is, not who they were signed in as. The explicit pick is
        // per session, so it is dropped.
        set({ session: null, runtimeMode: 'demo', anchorPickedByUser: false });
    },
    reloadSession: () => set({ session: loadStoredSession() }),
    setRuntimeMode: (runtimeMode) => set({ runtimeMode }),
}));
